/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useLayoutEffect, useRef, useMemo } from 'preact/hooks';
import { ComponentChildren } from 'preact';

import { Text } from '../UNSAFE_Text';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { useHover } from '../hooks/UNSAFE_useHover';
import { classNames } from '../utils/UNSAFE_classNames';
import { useId } from '../hooks/UNSAFE_useId';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { usePress } from '../hooks/UNSAFE_usePress';
import { getClientHints } from '../utils/PRIVATE_clientHints';

import { useMenuContext } from './useMenuContext';

const styles = {
  menuItemContainer: cssProps`${{
    cursor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-cursor-clickable) !important',
    width: '100%',
    paddingTop: 'var(--oj-PRIVATE-DO-NOT-USE-collection-list-cell-padding-vertical)',
    paddingBottom: 'var(--oj-PRIVATE-DO-NOT-USE-collection-list-cell-padding-vertical)',
    paddingInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-start-edge-to-start-icon-padding)',
    paddingInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-end-icon-to-end-edge-padding)',
    boxSizing: 'border-box',
    minHeight: 'var( --oj-c-PRIVATE-NOT-USE-collection-list-row-height)',
    display: 'flex',
    textAlign: 'start',
    alignItems: 'center',
    textDecorationLine: 'none !important',
    outlineStyle: 'none'
  }}`,
  focused: cssProps`${{
    outlineStyle: 'dotted',
    outlineOffset: '1px',
    outlineWidth: '1px',
    outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)'
  }}`,
  variant: {
    default: {
      menuItemContainer: cssProps`${{
        '&:active': {
          backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-active)'
        },
        color: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-text-color)'
      }}`,
      hover: cssProps`${{
        // @ts-ignore
        '&:not(:active)': {
          backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-hover)'
        }
      }}`,
      pseudohover: cssProps`${{
        // @ts-ignore
        '@media(hover: hover)': {
          '&:hover:not(:active)': {
            backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-hover)'
          }
        }
      }}`
    },
    destructive: {
      menuItemContainer: cssProps`${{
        //Not needed since hover and active share same style but active state style could change at some point
        '&:active': {
          backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-danger-hover)',
          color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-danger)'
        },
        color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-danger-low)'
      }}`,
      hover: cssProps`${{
        backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-danger-hover)',
        color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-danger)'
      }}`,
      pseudohover: cssProps`${{
        // @ts-ignore
        '@media(hover: hover)': {
          '&:hover': {
            backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-danger-hover)',
            color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-danger)'
          }
        }
      }}`
    }
  },
  iconLabelContainer: cssProps`${{
    display: 'flex',
    alignItems: 'center',
    overflowX: 'hidden',
    overflowY: 'hidden'
  }}`,
  labelContainer: cssProps`${{
    flexGrow: 1,
    minHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-icon-size)'
  }}`,
  iconContainer: cssProps`${{
    flexShrink: 0,
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-icon-size)',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-icon-size)',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-icon-size)'
  }}`,
  startIconContainer: cssProps`${{
    marginInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-start-icon-to-label-padding)'
  }}`,
  endIconContainer: cssProps`${{
    marginInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-menu-label-to-end-icon-padding)'
  }}`,
  disabledContainer: cssProps`${{
    cursor: 'default !important',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-disabled)'
  }}`
};

type BaseMenuItemProps = (SimpleMenuItemProps | SelectMenuItemProps) & BaseBaseMenuItemProps;

type BaseBaseMenuItemProps = {
  label: string;
  onAction?: () => void;
  startIcon?: ComponentChildren;
  endIcon?: ComponentChildren;
  isDisabled?: boolean;
};

type SimpleMenuItemProps = {
  role: 'menuitem';
  variant?: 'default' | 'destructive';
  isChecked?: never;
};

type SelectMenuItemProps = {
  role: 'menuitemcheckbox' | 'menuitemradio';
  variant?: never;
  isChecked?: boolean;
};

type MenuItemProps = BaseBaseMenuItemProps & {
  variant?: 'default' | 'destructive';
};

export function MenuItem(props: MenuItemProps) {
  return <BaseMenuItem {...props} role="menuitem"></BaseMenuItem>;
}

const isHybrid = getClientHints().isHybrid;

export function BaseMenuItem({
  label,
  onAction,
  startIcon,
  endIcon,
  role,
  isChecked,
  isDisabled = false,
  variant = 'default'
}: BaseMenuItemProps) {
  const { onClose, currentKey, showFocusRing } = useMenuContext();

  const uniqueID = useId();
  const id = useMemo(() => `oj-menu-item-${uniqueID}`, [uniqueID]);
  const isCurrent = currentKey === id;

  const menuItemRef = useRef<HTMLAnchorElement | null>(null);

  const { hoverProps, isHover } = useHover({ isDisabled: !isHybrid });

  const handleItemSelection = () => {
    onClose?.({ reason: 'itemAction' });
    onAction?.();
  };

  const { pressProps } = usePress(handleItemSelection, { isDisabled });

  useLayoutEffect(() => {
    if (isCurrent) {
      menuItemRef.current?.focus();
    }
  }, [isCurrent]);

  const menuItemContainerClasses = classNames([
    styles.menuItemContainer,
    !isDisabled && styles.variant[variant].menuItemContainer,
    isDisabled && styles.disabledContainer,
    !isHybrid && !isDisabled && styles.variant[variant].pseudohover,
    isHybrid && isHover && !isDisabled && styles.variant[variant].hover,
    isCurrent && !!showFocusRing && styles.focused
  ]);

  const startIconContainerClasses = classNames([
    styles.iconLabelContainer,
    styles.iconContainer,
    styles.startIconContainer
  ]);

  const labelContainerClasses = classNames([styles.iconLabelContainer, styles.labelContainer]);

  const endIconContainerClasses = classNames([
    styles.iconLabelContainer,
    styles.iconContainer,
    styles.endIconContainer
  ]);

  return (
    <a
      ref={menuItemRef}
      class={menuItemContainerClasses}
      {...mergeProps(hoverProps, pressProps)}
      role={role}
      data-oj-menu-item-key={id}
      {...(isDisabled && { 'aria-disabled': true })}
      {...(role !== 'menuitem' && { 'aria-checked': isChecked })}
      tabIndex={isCurrent ? 0 : -1}>
      {startIcon && <span class={startIconContainerClasses}>{startIcon}</span>}
      <span class={labelContainerClasses}>
        <Text variant="inherit" size="md">
          {label}
        </Text>
      </span>
      {endIcon && <span class={endIconContainerClasses}>{endIcon}</span>}
    </a>
  );
}
